import React from 'react';
import flowRunner, { type TFlowKind, type TFlowStartExtras, type TFlowState, type TFlowTransport } from '@besuites/auth/flow';
import { useBeAuthContext } from '@besuites/auth/react';
import type { TBeAuthBrowser } from '@besuites/auth/browser';
import flowScreenOf from '@/function/auth/flow.screen.of';
import flowAutoOf from '@/function/auth/flow.auto.of';
import type { TypeAuthFlow, TypeFlowChoice, TypeFlowFailure } from '@/types/auth/types.auth.flow';

type Options = {
    kind: TFlowKind;
    transport?: TFlowTransport;
    browser?: TBeAuthBrowser;
    initialChoice?: TypeFlowChoice;
    onDone?: (session: Readonly<Record<string, unknown>>) => void;
};


const failureOf = (state: TFlowState): TypeFlowFailure | null => {
    if (state.phase === 'error') return state.error;
    return state.phase === 'step' ? state.error ?? null : null;
};

export default function useAuthFlow({ kind, transport, browser, initialChoice = null, onDone }: Options): TypeAuthFlow {
    const context = useBeAuthContext();
    const source = transport ?? context.transport;
    const target = browser ?? context.browser;
    const [runner, setRunner] = React.useState(() => flowRunner(source));
    const state = React.useSyncExternalStore(runner.subscribe, runner.state, runner.state);
    const [choice, setChoice] = React.useState<TypeFlowChoice>(initialChoice);
    const [codesSeen, setCodesSeen] = React.useState(false);
    const [hint, setHint] = React.useState<string | null>(null);
    const emitted = React.useRef<Record<string, unknown>>({});
    const autoDone = React.useRef(new Set<string>());
    const doneSeen = React.useRef<TFlowState | null>(null);
    const adopted = React.useRef<TFlowState | null>(null);
    const onDoneRef = React.useRef(onDone);
    onDoneRef.current = onDone;

    if (state.phase === 'step') emitted.current = { ...emitted.current, ...state.emits };

    const failure = failureOf(state);
    const screen = flowScreenOf(state, emitted.current, choice, codesSeen);

    React.useEffect(() => {
        const auto = flowAutoOf(kind, state, choice);
        if (auto === null || state.phase !== 'step') return;
        const key = `${state.flowId}:${auto.step}`;
        if (autoDone.current.has(key)) return;
        autoDone.current.add(key);
        void runner.submit(auto.step, auto.input);
    }, [kind, state, choice, runner]);

    React.useEffect(() => {
        if (state.phase !== 'done' || adopted.current === state) return;
        adopted.current = state;
        const { accessToken, expiresIn } = state.session;
        if (typeof accessToken === 'string' && typeof expiresIn === 'number') target.signIn(accessToken, expiresIn);
    }, [state, target]);

    React.useEffect(() => {
        if (state.phase !== 'done' || screen.id !== 'done' || doneSeen.current === state) return;
        doneSeen.current = state;
        onDoneRef.current?.(state.session);
    }, [state, screen.id]);

    const start = React.useCallback((startHint?: string | null, extras?: TFlowStartExtras, nextChoice?: TypeFlowChoice) => {
        emitted.current = {};
        autoDone.current = new Set();
        setCodesSeen(false);
        setHint(startHint ?? null);
        if (nextChoice !== undefined) setChoice(nextChoice);
        return runner.start(kind, startHint ?? null, extras);
    }, [runner, kind]);

    const reset = React.useCallback(() => {
        emitted.current = {};
        autoDone.current = new Set();
        doneSeen.current = null;
        adopted.current = null;
        setCodesSeen(false);
        setHint(null);
        setChoice(initialChoice);
        setRunner(flowRunner(source));
    }, [initialChoice, source]);

    return {
        kind,
        state,
        emitted: emitted.current,
        screen,
        choice,
        failure,
        busy: state.phase === 'loading' || (state.phase === 'step' && state.busy !== undefined),
        hint,
        start,
        submit: runner.submit,
        choose: setChoice,
        acknowledgeCodes: () => setCodesSeen(true),
        reset,
    };
}
